import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';

const initialState = {
	data: {}, // org-scoped: data[orgID] = [...resources]
	timeStamp: null,

	loading: false,
	createResourceLoading: false,
	updateResourceLoading: false,
	deleteResourceLoading: false,
	resourcesLoadingComplete: false,

	// one-shot success signal so modals/drawers can close on mutation success
	mutationComplete: false,

	// Single resource, loaded on demand. The LIST endpoint omits accessChannels and
	// eligibilityTeamIDs, so an edit drawer seeded from a list row cannot see which access
	// channels a resource has — or their Cloud Key config. This holds the full record.
	currentResource: null,
	currentResourceLoading: false,

	// availability (per-resource free/busy lookup)
	availability: null,
	availabilityLoading: false,

	// share-email (send a resource's guest booking link)
	shareEmailSending: false,

	// blackout / maintenance windows (per-resource)
	blackouts: {}, // blackouts[resourceID] = [...]
	blackoutsLoading: false,
	blackoutMutating: false,
	blackoutMutationComplete: false,
	blackoutConflicts: null, // set when a create is blocked by overlapping bookings (offer override)

	// custom intake questions (per-resource)
	questions: {}, // questions[resourceID] = [...]
	questionsLoading: false,
	questionMutating: false,
	questionMutationComplete: false,

	error: null,
	searchable: true
};

const bookableResourcesSlice = createSlice({
	name: 'bookableResources',
	initialState,
	reducers: {
		// 🔥 Load resources
		loadResources: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadResourcesSuccess: (state, action) => {
			state.timeStamp = Date.now();
			state.loading = false;
			const orgID = action.payload.orgID;
			const oldData = state.data[orgID] || [];
			state.data[orgID] = mergeReduxStateData(oldData, action.payload.data, 'resourceID');
			state.resourcesLoadingComplete = true;
		},
		loadResourcesError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		// 🔥 Load ONE resource (full record: + accessChannels, eligibilityTeamIDs)
		loadResource: (state) => {
			state.currentResourceLoading = true;
			state.error = null;
		},
		loadResourceSuccess: (state, action) => {
			state.currentResourceLoading = false;
			state.currentResource = action.payload;
		},
		loadResourceError: (state, action) => {
			state.currentResourceLoading = false;
			state.error = action.payload;
		},
		clearCurrentResource: (state) => {
			state.currentResource = null;
			state.currentResourceLoading = false;
		},

		// 🔥 Create
		createResource: (state) => {
			state.createResourceLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		createResourceSuccess: (state) => {
			state.createResourceLoading = false;
			state.mutationComplete = true;
		},
		createResourceError: (state, action) => {
			state.createResourceLoading = false;
			state.error = action.payload;
		},

		// 🔥 Update
		updateResource: (state) => {
			state.updateResourceLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		updateResourceSuccess: (state) => {
			state.updateResourceLoading = false;
			state.mutationComplete = true;
		},
		updateResourceError: (state, action) => {
			state.updateResourceLoading = false;
			state.error = action.payload;
		},

		// 🔥 Delete
		deleteResource: (state) => {
			state.deleteResourceLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		deleteResourceSuccess: (state) => {
			state.deleteResourceLoading = false;
			state.mutationComplete = true;
		},
		deleteResourceError: (state, action) => {
			state.deleteResourceLoading = false;
			state.error = action.payload;
		},

		resetResourceMutationState: (state) => {
			state.mutationComplete = false;
			state.error = null;
		},

		// 🔥 Availability
		loadAvailability: (state) => {
			state.availabilityLoading = true;
			state.availability = null;
			state.error = null;
		},
		loadAvailabilitySuccess: (state, action) => {
			state.availabilityLoading = false;
			state.availability = action.payload;
		},
		loadAvailabilityError: (state, action) => {
			state.availabilityLoading = false;
			state.error = action.payload;
		},

		// 🔥 Share a resource's guest link by email
		shareResourceEmail: (state) => {
			state.shareEmailSending = true;
			state.error = null;
		},
		shareResourceEmailSuccess: (state) => {
			state.shareEmailSending = false;
		},
		shareResourceEmailError: (state, action) => {
			state.shareEmailSending = false;
			state.error = action.payload;
		},

		// 🔥 Blackout / maintenance windows
		loadBlackouts: (state) => {
			state.blackoutsLoading = true;
			state.error = null;
		},
		loadBlackoutsSuccess: (state, action) => {
			state.blackoutsLoading = false;
			state.blackouts[action.payload.resourceID] = action.payload.data || [];
		},
		loadBlackoutsError: (state, action) => {
			state.blackoutsLoading = false;
			state.error = action.payload;
		},
		createBlackout: (state) => {
			state.blackoutMutating = true;
			state.blackoutMutationComplete = false;
			state.blackoutConflicts = null;
			state.error = null;
		},
		createBlackoutSuccess: (state) => {
			state.blackoutMutating = false;
			state.blackoutMutationComplete = true;
		},
		// create blocked by overlapping active bookings — carry the conflicts so the UI can offer override
		createBlackoutBlocked: (state, action) => {
			state.blackoutMutating = false;
			state.blackoutConflicts = action.payload;
		},
		createBlackoutError: (state, action) => {
			state.blackoutMutating = false;
			state.error = action.payload;
		},
		deleteBlackout: (state) => {
			state.blackoutMutating = true;
			state.error = null;
		},
		deleteBlackoutSuccess: (state) => {
			state.blackoutMutating = false;
			// intentionally does NOT set blackoutMutationComplete — that flag clears the add-form, and a delete
			// must not wipe an operator's in-progress "Add blackout" input (the list refreshes via loadBlackouts).
		},
		deleteBlackoutError: (state, action) => {
			state.blackoutMutating = false;
			state.error = action.payload;
		},
		resetBlackoutState: (state) => {
			state.blackoutMutationComplete = false;
			state.blackoutConflicts = null;
			state.error = null;
		},

		// 🔥 Custom intake questions (per-resource)
		loadQuestions: (state) => {
			state.questionsLoading = true;
			state.error = null;
		},
		loadQuestionsSuccess: (state, action) => {
			state.questionsLoading = false;
			state.questions[action.payload.resourceID] = action.payload.data || [];
		},
		loadQuestionsError: (state, action) => {
			state.questionsLoading = false;
			state.error = action.payload;
		},
		createQuestion: (state) => {
			state.questionMutating = true;
			state.questionMutationComplete = false;
			state.error = null;
		},
		createQuestionSuccess: (state) => {
			state.questionMutating = false;
			state.questionMutationComplete = true;
		},
		createQuestionError: (state, action) => {
			state.questionMutating = false;
			state.error = action.payload;
		},
		updateQuestion: (state) => {
			state.questionMutating = true;
			state.error = null;
		},
		updateQuestionSuccess: (state) => {
			state.questionMutating = false;
		},
		updateQuestionError: (state, action) => {
			state.questionMutating = false;
			state.error = action.payload;
		},
		deleteQuestion: (state) => {
			state.questionMutating = true;
			state.error = null;
		},
		deleteQuestionSuccess: (state) => {
			state.questionMutating = false;
		},
		deleteQuestionError: (state, action) => {
			state.questionMutating = false;
			state.error = action.payload;
		},
		resetQuestionState: (state) => {
			state.questionMutationComplete = false;
			state.error = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const bookableResourcesActions = bookableResourcesSlice.actions;
export default bookableResourcesSlice.reducer;
